import { ForwardedRef, forwardRef } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { useDateFieldRhf } from './hooks/use-date-field-rhf';
import { DateField } from './date-field';
import { DateFieldRhfProps } from './date-field.types';
import { useDateFieldRhfValidation } from './hooks';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

/**
 * DateFieldRhf is a React component that integrates the DateField with React Hook Form.
 * It uses the Controller component to manage the form state and validation.
 *
 * @param props - The properties for the DateFieldRhf component.
 * @returns A DateField component wrapped with React Hook Form's Controller.
 */
export const DateFieldRhf = forwardRef(
  (props: DateFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { name, rules } = props;
    const formContext = useFormContext();
    return (
      <Controller
        name={name}
        control={formContext.control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={fieldFactory(props, ref)}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 **/
const fieldFactory = (props: DateFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
  const { rules, onBlur, invalidDateMessage, ...inputProps } = props;
  return ({ field, fieldState }: FieldProps) => {
    useDateFieldRhfValidation(props);
    const { displayValue, handleBlur, handleChange, handleValueChange } = useDateFieldRhf(
      field,
      props,
    );
    const mergedRefs = useMergeRefs([field.ref, ref]);

    return (
      <DateField
        {...inputProps}
        required={!!rules?.required}
        ref={mergedRefs}
        value={displayValue}
        onBlur={handleBlur}
        onChange={handleChange}
        onValueChange={handleValueChange}
        errorMessage={fieldState.error?.message?.toString()}
      />
    );
  };
};
